import { useTranslations } from "next-intl";
import { Loader } from "@/components/common/loader";
import { DietMenuModalAllergensAlert } from "@/features/diets/components/diet-menu-modal/diet-menu-modal-allergens-alert";
import { useMealIngredients } from "@/features/diets/hooks/use-meal-ingredients";
import type { DietMenuPreviewMeals } from "@/features/diets/types/diet-menu-preview.type";

type DietMenuModalIngredientsProps = {
  dietId: string;
  meals: DietMenuPreviewMeals[] | undefined;
  mealId: string | null;
  dishId?: string | null;
};

export const DietMenuModalIngredients = ({
  dietId,
  meals,
  mealId,
  dishId,
}: DietMenuModalIngredientsProps) => {
  const t = useTranslations();
  const meal = meals?.find((item) => item.mealId === mealId);
  const dish = dishId
    ? meal?.dishes.find((d) => d.id === dishId)
    : meal?.dishes.at(0);
  const { data, isError, isLoading } = useMealIngredients({
    dietId,
    ingredientsId: dish?.idForIngredients,
  });

  if (isLoading) {
    return <Loader />;
  }

  const ingredients =
    data?.ingredients && data?.ingredients?.length > 0
      ? data?.ingredients.join(", ")
      : "";

  const nutritionDetails = dish && [
    {
      label: t("nutrition.protein"),
      value: `${dish.nutrition.protein}g`,
    },
    {
      label: t("nutrition.carbs"),
      value: `${dish.nutrition.carbs}g`,
    },
    {
      label: t("nutrition.fat"),
      value: `${dish.nutrition.fat}g`,
    },
    {
      label: t("nutrition.roughage"),
      value: `${dish.nutrition.roughage}g`,
    },
    {
      label: t("nutrition.sugar"),
      value: `${dish.nutrition.sugar}g`,
    },
    {
      label: t("nutrition.salt"),
      value: `${dish.nutrition.salt}g`,
    },
    {
      label: t("nutrition.saturated_fatty_acids"),
      value: `${dish.nutrition.saturatedFattyAcids}g`,
    },
  ];

  return (
    <div className="flex flex-col justify-between gap-6 px-4 pt-6 pb-6 md:flex-row md:gap-10 md:px-18 md:pb-8">
      <DietMenuModalAllergensAlert
        allergens={dish?.allergens.allergens}
        className="rounded-xl p-4 md:hidden"
      />
      {nutritionDetails && (
        <div className="w-full shrink-0 md:w-68">
          <p className="font-medium text-xl">{t("nutrition.nutrition_text")}</p>
          <div className="flex flex-col pt-3">
            <span className="flex justify-between border-b p-2">
              <span className="font-normal">{t("common.kcalText")} </span>
              <span className="font-bold">
                {dish.kcal} {t("common.kcalString")}
              </span>
            </span>
            {nutritionDetails.map(({ label, value }) => (
              <span className="flex justify-between border-b p-2" key={label}>
                <span className="font-normal">{label} </span>
                <span className="font-bold">{value}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-4">
        <p className="font-medium text-xl">{t("diets.ingredients.title")}</p>
        <DietMenuModalAllergensAlert
          allergens={dish?.allergens.allergens}
          className="hidden rounded-xl p-4 md:flex md:items-start"
        />
        <div>
          {isError
            ? t("diets.ingredients.error")
            : ingredients || t("diets.ingredients.noIngredients")}
        </div>
      </div>
    </div>
  );
};
